<template>
  <div class="business-card" :class="`status-${business.status}`">
    <div class="card-header">
      <div class="business-logo">
        <i class="bi bi-building"></i>
      </div>
      <div class="business-basic">
        <h3 class="business-name">{{ business.business_name }}</h3>
        <div class="business-type">{{ business.application_type }}</div>
      </div>
      <div class="status-badge" :class="business.status?.toLowerCase()">
        {{ getStatusLabel(business.status) }}
      </div>
    </div>
    
    <div class="card-content">
      <div class="info-row">
        <i class="bi bi-hash"></i>
        <span class="label">License:</span>
        <span class="value">{{ business.bin }}</span>
      </div>
      <div class="info-row">
        <i class="bi bi-geo-alt"></i>
        <span class="label">Address:</span>
        <span class="value truncated">{{ business.barangay }}</span>
      </div>
      <div class="info-row">
        <i class="bi bi-person"></i>
        <span class="label">Owner:</span>
        <span class="value">{{ business.owner_fullname }}</span>
      </div>
      <div class="info-row">
        <i class="bi bi-calendar-event"></i>
        <span class="label">Year:</span>
        <span class="value" :class="{ 'expiring': isExpiringSoon(business.expiryDate) }">
          {{ business.year }}
        </span>
      </div>
      
      <div class="stats-grid">
        <div class="stat-item">
          <div class="stat-number">{{ business.employees }}</div>
          <div class="stat-label">Employees</div>
        </div>
        <div class="stat-item">
          <div class="stat-number">{{  Formatters.formatCurrency(business.capitalization) }}</div>
          <div class="stat-label">{{ business.application_type === 'New'? 'Capitalization' : 'Gross' }}</div>
        </div>
        <div class="stat-item">
          <div class="stat-number">{{ business.complianceScore }}%</div>
          <div class="stat-label">Compliance</div>
        </div>
      </div>
    </div>
    
    <div class="card-actions">
      <button class="action-btn view-btn" @click="$emit('view', business)">
        <i class="bi bi-eye"></i>
        View
      </button>
      <button 
        v-if="canRenew(business)"
        class="action-btn renew-btn"
        @click="$emit('renew', business)"
      >
        <i class="bi bi-arrow-clockwise"></i>
        Renew
      </button>
      <button v-if="canEdit(business)" class="action-btn manage-btn" @click="$emit('edit', business)">
        <i class="bi bi-pencil"></i>
        Edit
      </button>
    </div>
  </div>
</template>

<script setup>
import Formatters from '@/utils/formatters';

//eslint-disable-next-line
const props = defineProps({
  business: {
    type: Object,
    required: true
  }
});
//eslint-disable-next-line
defineEmits(['view', 'renew', 'edit']);

const getStatusLabel = (status) => {
  const labels = {
    active: 'Active',
    pending: 'Pending',
    renewal: 'Renewal Due',
    expired: 'Expired'
  };
  return labels[status] || status;
};

const canEdit = (business)=>{
  return (business.status?.toLowerCase() === 'pending')
}

const canRenew = (business) => {
    const currentYear = new Date().getFullYear().toString(); // "2024"
    
    if(business.year < currentYear && business.renewal_status?.toLowerCase() === "current") {
        // Business already renewed for current year
        return true;
    }
    
    return false;
};

//eslint-disable-next-line
const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const isExpiringSoon = (expiryDate) => {
  const expiry = new Date(expiryDate);
  const now = new Date();
  const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
  return expiry <= thirtyDaysFromNow && expiry > now;
};
</script>

<style scoped>
.business-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 2px solid #eaeaea;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  position: relative;
  animation: cardAppear 0.5s ease-out;
}

@keyframes cardAppear {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.business-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 40px rgba(183, 28, 28, 0.15);
  border-color: #b71c1c;
}

/* Status-based border colors */
.business-card.status-active {
  border-color: #4caf50;
}

.business-card.status-pending {
  border-color: #ff9800;
}

.business-card.status-renewal {
  border-color: #2196f3;
}

.business-card.status-expired {
  border-color: #f44336;
}

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.5rem 1.5rem 1rem;
  border-bottom: 1px solid #f0f0f0;
  position: relative;
}

.business-logo {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #b71c1c, #d32f2f);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;
}

.business-basic {
  flex: 1;
  min-width: 0;
}

.business-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: #333;
  margin: 0 0 0.25rem 0;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.business-type {
  font-size: 0.9rem;
  color: #666;
  font-weight: 500;
}

.status-badge {
  position: absolute;
  top: 1.5rem;
  right: 1.5rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.status-badge.active {
  background: rgba(76, 175, 80, 0.1);
  color: #2e7d32;
}
.status-badge.checking {
  background: rgba(76, 175, 80, 0.1);
  color: #7d2e65;
}

.status-badge.pending {
  background: rgba(255, 152, 0, 0.1);
  color: #f57c00;
}

.status-badge.renewal {
  background: rgba(33, 150, 243, 0.1);
  color: #1976d2;
}

.status-badge.expired {
  background: rgba(244, 67, 54, 0.1);
  color: #d32f2f;
}

.card-content {
  padding: 1.5rem;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
}

.info-row i {
  width: 16px;
  color: #b71c1c;
  font-size: 0.9rem;
}

.info-row .label {
  color: #666;
  font-weight: 500;
  min-width: 70px;
}

.info-row .value {
  color: #333;
  font-weight: 600;
  flex: 1;
}

.info-row .value.expiring {
  color: #f57c00;
  font-weight: 700;
}

.truncated {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #f0f0f0;
}

.stat-item {
  text-align: center;
}

.stat-number {
  font-size: 1.25rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.75rem;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-actions {
  display: flex;
  gap: 0.5rem;
  padding: 1rem 1.5rem 1.5rem;
  background: linear-gradient(to top, #fafafa, transparent);
}

.action-btn {
  flex: 1;
  padding: 0.75rem;
  border: 2px solid #e0e0e0;
  border-radius: 10px;
  background: white;
  color: #666;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.action-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.view-btn:hover {
  border-color: #2196f3;
  color: #1976d2;
}

.renew-btn:hover {
  border-color: #4caf50;
  color: #2e7d32;
}

.manage-btn:hover {
  border-color: #b71c1c;
  color: #d32f2f;
}
</style>